// The tabs (02-DESIGN/03-console/01-the-console.md § conventions: tabs; § page
// anatomy). A page that holds more than one concern holds each in a tab under
// the title and its one line, never stacked as sections. The first tab is
// what a person comes for most and the page opens on it; the page's primary
// action belongs to the tab and changes with it (the page passes the current
// tab's action to its PageHead); the title and its line do not. A tab is a
// view of the page and not in the address: a link to the page opens its
// first tab, which is what `useTab` starts on. Every tab stands alone (§
// conventions: a reply it cannot draw): one that cannot draw a reply says so
// in its panel, and the title and the other tabs stay.

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { Boundary } from "./boundary.tsx";
import "./table.css";

/** TabSpec is one tab: its id and its name, in one or two words. */
export interface TabSpec<T extends string> {
  id: T;
  label: string;
  /** A dot beside the name: the tab holds changes not saved yet (§ the blueprint's page). */
  changed?: boolean;
}

/**
 * useTab is a page's current tab, opening on the first — or on `initial`,
 * the tab a link asked for in its navigation state; never in the address.
 */
export function useTab<T extends string>(tabs: readonly TabSpec<T>[], initial?: T): [T, (t: T) => void] {
  const first = tabs[0]?.id;
  if (first === undefined) {
    throw new Error("useTab needs at least one tab");
  }
  const [tab, setTab] = useState<T>(initial ?? first);
  // A tab the role no longer has falls back to the first.
  const current = tabs.some((t) => t.id === tab) ? tab : first;
  return [current, setTab];
}

/**
 * Tabs draws the tab list and the current tab's panel. The arrow keys move
 * between tabs and select them, Home and End go to the ends; only the
 * selected tab is in the tab order, and the panel follows it.
 */
export function Tabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
  children,
}: {
  /** What the tabs divide, for a screen reader: the page's title. */
  label: string;
  tabs: readonly TabSpec<T>[];
  value: T;
  onChange: (t: T) => void;
  /** The current tab's content. */
  children: ReactNode;
}) {
  const base = useId();
  const list = useRef<HTMLDivElement>(null);
  const tabId = (t: T) => `${base}-tab-${t}`;
  const panelId = `${base}-panel`;

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.findIndex((t) => t.id === value);
    let next = -1;
    if (e.key === "ArrowRight") {
      next = (at + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      next = (at - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = tabs.length - 1;
    }
    const t = tabs[next];
    if (t === undefined) {
      return;
    }
    e.preventDefault();
    onChange(t.id);
    list.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabId(t.id))}`)?.focus();
  };

  return (
    <>
      <div ref={list} className="tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
        {tabs.map((t) => (
          <button
            key={t.id}
            id={tabId(t.id)}
            type="button"
            role="tab"
            className="tab"
            aria-selected={t.id === value}
            aria-controls={panelId}
            tabIndex={t.id === value ? 0 : -1}
            onClick={() => {
              onChange(t.id);
            }}
          >
            {t.label}
            {t.changed === true && (
              <>
                <span className="tab-dot" aria-hidden="true" />
                <span className="visually-hidden"> (not saved)</span>
              </>
            )}
          </button>
        ))}
      </div>
      <div id={panelId} role="tabpanel" aria-labelledby={tabId(value)}>
        <Boundary part="tab" at={value}>
          {children}
        </Boundary>
      </div>
    </>
  );
}
